import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EvalSheet from './EvalSheet';
import PrintButton from './PrintButton';
import PanneauEvalSuivi from './PanneauEvalSuivi';
import EmptyState from './EmptyState';
import PrintExo from './PrintExo';
import PrintQcmGroup from './renderers/PrintQcmGroup';
import PrintSaisieGroup from './renderers/PrintSaisieGroup';
import PrintVraiFauxGroup from './renderers/PrintVraiFauxGroup';
import PrintFigureGroup from './renderers/PrintFigureGroup';
import CompactHeader from './CompactHeader';
import StudentFields from './StudentFields';
import { buildCorrectionItems, CorrectionNode, CorrigeHeading } from './CorrectionsSection';
import { CarteAide, PlancheHeading } from './PlancheAidesSection';
import { construireCartesAide } from '../../utils/plancheAides';
import { groupQuestions } from './helpers/groupQuestions';
import {
    USABLE_PAGE_HEIGHT,
    SECTION_GAP,
    SAFETY,
    FALLBACK_CHROME_H,
    FALLBACK_STUDENT_H,
} from './helpers/pageGeometry';
import {
    composeSections,
    paginateByHeights,
    renumberBlocks,
    findAllOversizedFixes,
    replaceBlock,
    rebalanceMasonryFromDom,
    resolveUidSetting,
    rootUidOf,
    ZOOM_LADDER_PROF,
} from './helpers/packBlocks';
import ExoControls from './ExoControls';
import { sanitizeTitleForPdf } from './consignes';
import { reponseLibreDuBloc } from './helpers/reponseLibre';
import { LargeurBloc } from './helpers/largeurBloc';
import './eval-print.css';

/** Rend un bloc (group ou single) via le renderer approprié.
 *  `reponseLibre` (axe format) ne concerne que les QCM ; les autres
 *  renderers l'ignorent. */
const BlockRenderer = ({ block, saisieMode, reponseLibre = false }) => {
    if (block.kind === 'qcm-group') {
        return <PrintQcmGroup exNum={block.exNum} items={block.items} saisieMode={saisieMode} reponseLibre={reponseLibre} figurePartagee={block.figurePartagee} />;
    }
    if (block.kind === 'saisie-group') {
        return <PrintSaisieGroup exNum={block.exNum} items={block.items} saisieMode={saisieMode} figurePartagee={block.figurePartagee} />;
    }
    if (block.kind === 'vf-group') {
        return <PrintVraiFauxGroup exNum={block.exNum} items={block.items} saisieMode={saisieMode} reponseLibre={reponseLibre} figurePartagee={block.figurePartagee} />;
    }
    if (block.kind === 'figure-group') {
        return <PrintFigureGroup exNum={block.exNum} items={block.items} saisieMode={saisieMode} reponseLibre={reponseLibre} figurePartagee={block.figurePartagee} />;
    }
    return (
        <PrintExo
            payload={block.payload}
            num={block.exNum}
            saisieMode={saisieMode}
            reponseLibre={reponseLibre}
        />
    );
};

/** Types d'exercices « toujours pleine largeur » par défaut (l'élève trace au
 *  crayon — la précision exige la pleine largeur). Le prof peut les forcer en
 *  demi via ⇆, avec avertissement. */
const isAlwaysFullBlock = (block) => {
    const subs = block?.kind === 'single'
        ? [block.payload?.contenu?.subType]
        : block?.kind === 'figure-group'
            ? (block.items || []).map(({ payload }) => payload?.contenu?.subType)
            : [];
    return subs.some((sub) => sub === 'programme_construction' || sub === 'placer_points_2d');
};

/** Rend une section (full / single / masonry) via le BlockRenderer approprié.
 *
 *  Chaque bloc est enveloppé dans un `.ep-exo-slot` (position:relative,
 *  data-exo-uid) contenant :
 *    - `ExoControls` (FRÈRE du wrapper de zoom — absolu, no-print, hauteur
 *      nulle : rendu dans les DEUX phases, aucune influence sur la mesure) ;
 *    - `.ep-exo-zoom` avec `--ep-zoom` = facteur EFFECTIF (min manuel/auto)
 *      quand il diffère de 1.
 *  `ctl` = contrôleur de layout fourni par EvalPrintPage (résolutions par uid
 *  + callbacks de réglage). */
const SectionRenderer = ({ section, saisieMode, ctl }) => {
    const renderBlock = (block, isHalf) => {
        const uid = block.uid;
        const effective = ctl.effectiveFor(uid);
        // Axe format : un QCM à choix textuels ou un tableau à cocher peut
        // être rendu en réponse libre ; un QCM à images ou un tableau
        // d'association garde son support quel que soit le réglage.
        const rl = reponseLibreDuBloc(block);
        const reponse = rl.applicable ? ctl.reponseFor(uid) : null;
        const reponseLibre = reponse === 'libre' && rl.convertible;
        const node = (
            <BlockRenderer
                key={`b-${uid}`}
                block={block}
                saisieMode={saisieMode}
                reponseLibre={reponseLibre}
            />
        );
        const zoomed = effective < 1 - 1e-6 ? (
            <div className="ep-exo-zoom" style={{ '--ep-zoom': effective }}>
                {node}
            </div>
        ) : node;
        // Largeur du bloc pour les SVG à géométrie fixe (droite graduée,
        // compas) : même Provider dans les deux phases → DOM identique.
        const inner = (
            <LargeurBloc.Provider value={isHalf ? 'half' : 'full'}>
                {zoomed}
            </LargeurBloc.Provider>
        );
        return (
            <div key={`slot-${uid}`} className="ep-exo-slot" data-exo-uid={uid}>
                <ExoControls
                    uid={uid}
                    manualFactor={ctl.manualFor(uid)}
                    effectiveFactor={effective}
                    isHalf={isHalf}
                    forced={ctl.forcedFor(uid)}
                    refused={ctl.refusedFor(uid)}
                    isAlwaysFull={isAlwaysFullBlock(block)}
                    onBump={ctl.onBump}
                    onToggleDensity={ctl.onToggleDensity}
                    reponse={reponse}
                    famille={rl.famille}
                    reponseGlobal={ctl.reponseGlobal}
                    reponseConvertible={rl.convertible}
                    reponseRaison={rl.raison}
                    onToggleReponse={ctl.onToggleReponse}
                />
                {inner}
            </div>
        );
    };
    if (section.type === 'full') return renderBlock(section.block, false);
    if (section.type === 'single') return <>{section.blocks.map((b) => renderBlock(b, false))}</>;
    // masonry
    return (
        <div className="ep-masonry">
            <div className="ep-col">{section.leftCol.map((b) => renderBlock(b, true))}</div>
            <div className="ep-col">{section.rightCol.map((b) => renderBlock(b, true))}</div>
        </div>
    );
};

/**
 * Ajustement responsive de la feuille AFFICHÉE.
 *
 * La feuille garde toujours sa largeur A4 réelle (CSS `width: 21cm`) : c'est
 * la condition pour que mesure écran = rendu print. Quand l'écran est plus
 * étroit que l'A4, on ne reflowe pas (ce serait un autre document) : on
 * RÉDUIT la feuille visuellement (`transform: scale`), comme un lecteur PDF.
 * Le transform ne change pas le flux → le wrapper reçoit la hauteur réduite
 * pour que la page défile juste. Jamais appliqué à la feuille de MESURE
 * (absolue, hors flux, clippée par .ep-root). Neutralisé en @media print.
 */
const useSheetFit = (active) => {
    const wrapRef = useRef(null);
    const sheetRef = useRef(null);
    const [fit, setFit] = useState({ scale: 1, height: 0 });
    useEffect(() => {
        if (!active) return undefined;
        const wrap = wrapRef.current;
        const sheet = sheetRef.current;
        if (!wrap || !sheet) return undefined;
        const compute = () => {
            const avail = wrap.clientWidth;
            const w = sheet.offsetWidth;
            const h = sheet.offsetHeight;
            const scale = w > 0 && avail < w ? avail / w : 1;
            setFit((prev) => (prev.scale === scale && prev.height === h ? prev : { scale, height: h }));
        };
        compute();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', compute);
            return () => window.removeEventListener('resize', compute);
        }
        const ro = new ResizeObserver(compute);
        ro.observe(wrap);
        ro.observe(sheet);
        return () => ro.disconnect();
    }, [active]);
    return { wrapRef, sheetRef, fit };
};

const STORAGE_KEY = 'evalPrintData';
// Réglages de mise en page du prof (zoom/densité par exercice) — clé sœur,
// restaurée au chargement si la signature de l'éval correspond (un F5 réflexe
// après 8 réglages ne doit rien perdre).
const LAYOUT_KEY = 'evalPrintLayout';
const GOOGLE_FONTS_HREF =
    'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&'
    + 'family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&'
    + 'family=JetBrains+Mono:wght@500;600&display=swap';

// Préchargement des fonts KaTeX (déclenché AVANT toute mesure pour éviter
// que `document.fonts.ready` ne résolve avant que KaTeX ait trigger ses fonts).
const KATEX_FONTS = [
    'KaTeX_Main-Regular',
    'KaTeX_Main-Italic',
    'KaTeX_Math-Italic',
    'KaTeX_Size1-Regular',
    'KaTeX_Size2-Regular',
];

// Géométrie de page : la SEULE constante est la hauteur utile A4
// (helpers/pageGeometry.js). Le chrome (header, bandeau élève, padding de
// page) est MESURÉ au pixel via des sondes rendues dans la feuille témoin —
// plus aucune hauteur devinée.

/**
 * Route /print-eval — feuille d'évaluation imprimable avec **pagination
 * DOM-based**.
 *
 * Pipeline en 2 phases :
 *   1. **measure** : on rend toutes les sections invisibles (visibility: hidden)
 *      dans une .ep-sheet réelle (mêmes dimensions qu'en print). Après que les
 *      fonts (custom + KaTeX) et images sont chargées, on mesure chaque section
 *      via `getBoundingClientRect().height`.
 *   2. **display** : on pagine les sections d'après les vraies hauteurs (via
 *      `paginateByHeights`), on renumérote les blocs dans l'ordre visuel, on
 *      affiche définitivement.
 *
 * Avantage : précision parfaite — pas d'estimation, pas de calibration.
 * `getBoundingClientRect` retourne ce que le navigateur va imprimer.
 *
 * Mode intégré (`donnees` fourni, 2026-10-04) : un document de plusieurs
 * sujets (GroupesPrintPage) monte une instance par sujet. Données reçues en
 * prop au lieu de sessionStorage, réglages sous leur propre clé, sans barre
 * ni titre d'onglet ; `parties` choisit ce qui s'affiche (sujet, corrigé,
 * coups de pouce). Sans ces props, rien ne change. La barre de réglages est
 * celle du document parent, qui diffuse chaque clic à toutes les instances
 * par `ordre` ({ n, type, valeur }, n croissant).
 */
const TOUTES_PARTIES = { sujet: true, corrige: true, aides: true };
const EvalPrintPage = ({ donnees = null, cleReglages = '', parties = TOUTES_PARTIES, onPret = null, ordre = null }) => {
    const integre = donnees !== null;
    const cleLayout = LAYOUT_KEY + cleReglages;
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [phase, setPhase] = useState('measure');
    const [pages, setPages] = useState(null);
    const sectionRefs = useRef([]);
    const measureRootRef = useRef(null);
    // Sondes de chrome : mesurent le VRAI CompactHeader et le VRAI bandeau
    // élève (avec leurs marges — display:flow-root les inclut dans la boîte)
    const chromeProbeRef = useRef(null);
    const studentProbeRef = useRef(null);
    // Sondes du corrigé : en-tête « Corrigé » + un ref par item (indexé par id)
    const corrHeadingProbeRef = useRef(null);
    const corrItemRefs = useRef([]);
    const aidesHeadingProbeRef = useRef(null);
    const aideItemRefs = useRef([]);

    // Lecture sessionStorage au montage. La clé N'EST PLUS supprimée : un F5
    // sur l'onglet recharge l'éval (avant : EmptyState, tout était perdu).
    // sessionStorage est par-onglet, la donnée meurt avec lui.
    useEffect(() => {
        if (donnees) {
            setData(donnees);
            setLoaded(true);
            return;
        }
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (raw) {
                setData(JSON.parse(raw));
            }
        } catch (e) {
            // JSON invalide → reste null → EmptyState
        }
        setLoaded(true);
    }, [donnees]);

    // Chargement Google Fonts (Plus Jakarta Sans, Fraunces, JetBrains Mono)
    useEffect(() => {
        const id = 'ep-google-fonts';
        if (document.getElementById(id)) return;
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = GOOGLE_FONTS_HREF;
        document.head.appendChild(link);
    }, []);

    // Force le chargement des fonts KaTeX au montage en injectant un noeud
    // invisible qui les utilise. Sans ça, document.fonts.ready peut résoudre
    // avant que KaTeX ait demandé ses fonts → mesures avec fallback fonts → fausses.
    useEffect(() => {
        const id = 'ep-katex-font-trigger';
        if (document.getElementById(id)) return;
        const trigger = document.createElement('div');
        trigger.id = id;
        trigger.style.position = 'absolute';
        trigger.style.left = '-9999px';
        trigger.style.top = '0';
        trigger.style.visibility = 'hidden';
        trigger.style.fontSize = '14px';
        trigger.innerHTML = KATEX_FONTS.map(
            (f) => `<span style="font-family:'${f}',serif">A</span>`
        ).join('');
        document.body.appendChild(trigger);
        return () => trigger.remove();
    }, []);

    // document.title dynamique (nom de fichier PDF par défaut)
    useEffect(() => {
        if (!data?.meta || integre) return undefined;
        const { titre, classe, date, documentType } = data.meta;
        const prefix = documentType === 'entrainement' ? 'Exo' : 'Eval';
        const parts = [
            prefix,
            sanitizeTitleForPdf(titre),
            sanitizeTitleForPdf(classe),
            date || '',
        ].filter(Boolean);
        document.title = parts.join('_');
        return () => {
            document.title = 'TanoEd';
        };
    }, [data, integre]);

    // Composition (groupage + mise en sections) — pas de pagination ici
    const meta = data?.meta || {};
    const saisieMode = meta.saisieMode || 'sur_la_feuille';
    const isEntrainement = meta.documentType === 'entrainement';
    // Planche de coups de pouce à découper (option décochée par défaut).
    // PRIMITIF, jamais l'objet meta : `meta` est recréé à chaque rendu par
    // `data?.meta || {}`, mémoïser dessus relancerait l'effet sans fin.
    const avecAides = meta.coupsDePouce === true;
    const initialBlocks = useMemo(
        () => (data?.selectedQuestions ? groupQuestions(data.selectedQuestions) : []),
        [data]
    );

    // Blocs courants (mutables : peuvent être splittés en cours de boucle de mesure)
    // Et maxHalves (réduit dynamiquement si une masonry dépasse)
    // Et scaledBlocks : Map<uid, factor> — couche de réduction AUTO du moteur
    // (les réglages PROF vivent dans manualZoom/forcedDensity ci-dessous ;
    // le facteur appliqué est min(manuel, auto), résolu par chaîne d'uid).
    const [currentBlocks, setCurrentBlocks] = useState([]);
    const [maxHalves, setMaxHalves] = useState(6);
    const [scaledBlocks, setScaledBlocks] = useState(new Map());
    // Réglages prof — keyés sur les uid RACINE uniquement (les enfants de
    // split dérivent via resolveUidSetting, ne possèdent rien).
    const [manualZoom, setManualZoom] = useState(new Map());
    const [forcedDensity, setForcedDensity] = useState(new Map());
    // Mémo des demandes de demi REFUSÉES : Set<'rootUid@facteur'> — un uid
    // refusé à un facteur donné n'est plus retesté tant que le facteur ne
    // change pas (économise des itérations de la boucle de convergence).
    const [refusedHalves, setRefusedHalves] = useState(new Set());
    // Mode de densité : 'confort' (rendu historique, DÉFAUT — préservé pour
    // les élèves à difficultés de lecture) | 'compact' (classe .ep-compact :
    // options QCM nues, filets au lieu de cadres, consignes resserrées).
    const [densityMode, setDensityMode] = useState('confort');
    // Axe FORMAT (jumeau de la densité) : 'qcm' (rendu de la banque, DÉFAUT)
    // | 'libre' (QCM textuels → questions à réponse libre). Global +
    // override par exercice racine, LOCAL à l'aperçu (la banque ne bouge pas).
    const [reponseMode, setReponseMode] = useState('qcm');
    const [forcedReponse, setForcedReponse] = useState(new Map());
    const [layoutNotice, setLayoutNotice] = useState(null);
    // Rollback : snapshot des réglages AVANT la dernière action prof — si la
    // boucle n'arrive pas à converger, on restaure TOUT (réglages + moteur),
    // jamais un badge qui ment sur le rendu.
    const rollbackRef = useRef(null);
    // uid racine du dernier exercice réglé (flash + ancrage après re-dispatch)
    const lastAdjustedRef = useRef(null);
    // Pages du corrigé : tableaux d'ids d'items (cf. buildCorrectionItems)
    const [pagesCorrige, setPagesCorrige] = useState(null);
    const [pagesAides, setPagesAides] = useState(null);

    // Signature de l'éval — les réglages persistés ne sont restaurés que pour
    // la même éval (pas de fuite d'une éval à l'autre dans le même onglet).
    // Titre + identité des questions (id, repli position) : deux évals de
    // même taille et même titre ne partagent plus leurs réglages.
    const evalSig = useMemo(() => (
        data?.selectedQuestions
            ? `${data?.meta?.titre || ''}|${data.selectedQuestions.map((q) => q?.id ?? `p${q?.__position}`).join(',')}`
            : null
    ), [data]);

    // Initialise/reset les blocs quand la sélection change — puis restaure les
    // réglages prof persistés si la signature correspond.
    useEffect(() => {
        setCurrentBlocks(initialBlocks);
        setMaxHalves(6);
        setScaledBlocks(new Map());
        setRefusedHalves(new Set());
        setLayoutNotice(null);
        rollbackRef.current = null;
        let manual = new Map();
        let forced = new Map();
        let mode = 'confort';
        let reponse = 'qcm';
        let forcedRep = new Map();
        try {
            const raw = sessionStorage.getItem(cleLayout);
            if (raw && evalSig) {
                const saved = JSON.parse(raw);
                if (saved?.sig === evalSig) {
                    manual = new Map(saved.manual || []);
                    forced = new Map(saved.forced || []);
                    if (saved.mode === 'compact') mode = 'compact';
                    if (saved.reponse === 'libre') reponse = 'libre';
                    forcedRep = new Map(saved.forcedReponse || []);
                }
            }
        } catch (e) { /* réglages illisibles → repartir à neuf */ }
        setManualZoom(manual);
        setForcedDensity(forced);
        setDensityMode(mode);
        setReponseMode(reponse);
        setForcedReponse(forcedRep);
        setPages(null);
        setPagesCorrige(null);
        setPagesAides(null);
        setPhase('measure');
    }, [initialBlocks, evalSig, cleLayout]);

    // Persistance des réglages prof (clé sœur de l'éval, par onglet)
    useEffect(() => {
        if (!evalSig) return;
        try {
            sessionStorage.setItem(cleLayout, JSON.stringify({
                sig: evalSig,
                manual: Array.from(manualZoom.entries()),
                forced: Array.from(forcedDensity.entries()),
                mode: densityMode,
                reponse: reponseMode,
                forcedReponse: Array.from(forcedReponse.entries()),
            }));
        } catch (e) { /* quota plein → tant pis, réglages non persistés */ }
    }, [manualZoom, forcedDensity, densityMode, reponseMode, forcedReponse, evalSig, cleLayout]);

    // Items du corrigé — identités STABLES (useMemo sur currentBlocks) pour
    // les deps d'effet ; les numéros restent justes car <CorrectionNode> lit
    // block.exNum AU RENDU (post-renumber). Hauteurs raccrochées par `id`.
    const correctionItems = useMemo(() => buildCorrectionItems(currentBlocks), [currentBlocks]);

    // Cartes de coup de pouce — même discipline que les items du corrigé :
    // identités stables, et les numéros d'exercice sont lus AU RENDU par
    // <CarteAide> (post-renumber). Vide tant que l'option n'est pas cochée,
    // donc aucune sonde rendue et aucune page ajoutée.
    const cartesAide = useMemo(
        () => (avecAides ? construireCartesAide(currentBlocks) : []),
        [currentBlocks, avecAides]
    );

    // ─── Résolutions par uid (chaîne de suffixes de split) ────────────
    // Facteur AUTO (moteur), MANUEL (prof, plafond), EFFECTIF = min des deux.
    const autoFor = useCallback(
        (uid) => resolveUidSetting(scaledBlocks, uid) ?? 1,
        [scaledBlocks]
    );
    const manualFor = useCallback(
        (uid) => resolveUidSetting(manualZoom, uid) ?? 1,
        [manualZoom]
    );
    const effectiveFor = useCallback(
        (uid) => Math.min(manualFor(uid), autoFor(uid)),
        [manualFor, autoFor]
    );
    const refusedFor = useCallback(
        (uid) => refusedHalves.has(`${rootUidOf(uid)}@${effectiveFor(uid)}`),
        [refusedHalves, effectiveFor]
    );
    // Densité forcée résolue, filtrée des refus : une demande de demi refusée
    // au facteur courant rend la main aux règles automatiques (→ full).
    const forcedFor = useCallback(
        (uid) => {
            const raw = resolveUidSetting(forcedDensity, uid);
            if (raw === 'half' && refusedFor(uid)) return null;
            return raw ?? null;
        },
        [forcedDensity, refusedFor]
    );
    // Format résolu : override racine sinon réglage global.
    const reponseFor = useCallback(
        (uid) => resolveUidSetting(forcedReponse, uid) ?? reponseMode,
        [forcedReponse, reponseMode]
    );

    const sections = useMemo(
        () => composeSections(currentBlocks, {
            isEntrainement,
            saisieMode,
            maxHalvesPerMasonry: maxHalves,
            forcedDensityFor: forcedFor,
            zoomFor: (block) => effectiveFor(block.uid),
            reponseFor,
        }),
        [currentBlocks, isEntrainement, saisieMode, maxHalves, forcedFor, effectiveFor, reponseFor]
    );

    // ─── Actions prof (contrôles au survol) ───────────────────────────
    // Chaque action : snapshot pour rollback, purge de la couche auto (le
    // moteur re-dérive ses réductions dans le nouveau contexte — c'est ce qui
    // donne un effet réel à A+), reset du mémo de refus de l'uid touché, et
    // relance du cycle mesure.
    const snapshotForRollback = useCallback(() => {
        rollbackRef.current = {
            manualZoom,
            forcedDensity,
            reponseMode,
            forcedReponse,
            scaledBlocks,
            maxHalves,
            currentBlocks,
            refusedHalves,
        };
    }, [manualZoom, forcedDensity, reponseMode, forcedReponse, scaledBlocks, maxHalves, currentBlocks, refusedHalves]);

    const relancerCycle = useCallback(() => {
        iterationRef.current = 0;
        setScaledBlocks(new Map());
        setLayoutNotice(null);
        setPhase('measure');
    }, []);

    const onBump = useCallback((uid, dir) => {
        const root = rootUidOf(uid);
        const cur = resolveUidSetting(manualZoom, root) ?? 1;
        let idx = ZOOM_LADDER_PROF.findIndex((f) => Math.abs(f - cur) < 1e-6);
        if (idx === -1) idx = 0;
        const nextIdx = Math.min(ZOOM_LADDER_PROF.length - 1, Math.max(0, idx + dir));
        if (nextIdx === idx) return;
        snapshotForRollback();
        const next = ZOOM_LADDER_PROF[nextIdx];
        const map = new Map(manualZoom);
        if (next >= 1 - 1e-6) map.delete(root); else map.set(root, next);
        setManualZoom(map);
        lastAdjustedRef.current = root;
        relancerCycle();
    }, [manualZoom, snapshotForRollback, relancerCycle]);

    const onToggleDensity = useCallback((uid, versHalf) => {
        const root = rootUidOf(uid);
        snapshotForRollback();
        const map = new Map(forcedDensity);
        map.set(root, versHalf ? 'half' : 'full');
        setForcedDensity(map);
        if (versHalf) {
            // Nouvelle demande explicite → on laisse le moteur retester
            setRefusedHalves((prev) => {
                const s = new Set([...prev].filter((k) => !k.startsWith(`${root}@`)));
                return s;
            });
        }
        lastAdjustedRef.current = root;
        relancerCycle();
    }, [forcedDensity, snapshotForRollback, relancerCycle]);

    // Axe format par exercice : un override identique au global est
    // SUPPRIMÉ (pas d'exception redondante → pas de badge).
    const onToggleReponse = useCallback((uid, mode) => {
        const root = rootUidOf(uid);
        snapshotForRollback();
        const map = new Map(forcedReponse);
        if (mode === reponseMode) map.delete(root); else map.set(root, mode);
        setForcedReponse(map);
        lastAdjustedRef.current = root;
        relancerCycle();
    }, [forcedReponse, reponseMode, snapshotForRollback, relancerCycle]);

    // Axe format global : écrase les overrides (le prof repart d'une base
    // uniforme, puis affine par carte).
    const onGlobalReponse = useCallback((mode) => {
        if (mode === reponseMode && forcedReponse.size === 0) return;
        snapshotForRollback();
        setReponseMode(mode);
        setForcedReponse(new Map());
        lastAdjustedRef.current = null;
        relancerCycle();
    }, [reponseMode, forcedReponse, snapshotForRollback, relancerCycle]);

    const onResetLayout = useCallback(() => {
        setManualZoom(new Map());
        setForcedDensity(new Map());
        setReponseMode('qcm');
        setForcedReponse(new Map());
        setRefusedHalves(new Set());
        setMaxHalves(6);
        setCurrentBlocks(initialBlocks);
        setDensityMode('confort');
        rollbackRef.current = null;
        lastAdjustedRef.current = null;
        try { sessionStorage.removeItem(cleLayout); } catch (e) { /* no-op */ }
        relancerCycle();
    }, [initialBlocks, relancerCycle, cleLayout]);

    // Bascule Confort/Compact : le CSS change presque toutes les hauteurs →
    // cycle complet (mesure + re-dispatch + re-pagination). Le mémo de refus
    // est purgé (les largeurs de colonnes changent aussi).
    const onToggleMode = useCallback(() => {
        setDensityMode((prev) => (prev === 'compact' ? 'confort' : 'compact'));
        setRefusedHalves(new Set());
        rollbackRef.current = null;
        relancerCycle();
    }, [relancerCycle]);

    // ─── Réglages GLOBAUX (barre) : écriture en masse dans les mêmes Maps —
    // badges, plafond/refus, rollback et persistance suivent sans code
    // supplémentaire, et les contrôles par carte restent dispo pour affiner.
    const rootUids = useMemo(() => initialBlocks.map((b) => b.uid), [initialBlocks]);

    const onGlobalZoom = useCallback((factor) => {
        snapshotForRollback();
        if (factor >= 1 - 1e-6) {
            setManualZoom(new Map());
        } else {
            setManualZoom(new Map(rootUids.map((uid) => [uid, factor])));
        }
        lastAdjustedRef.current = null;
        relancerCycle();
    }, [rootUids, snapshotForRollback, relancerCycle]);

    const onGlobalHalf = useCallback(() => {
        snapshotForRollback();
        // Les exercices à tracé (programme de construction, placer des points)
        // sont ÉPARGNÉS : le forçage global ne dégrade pas la précision du
        // crayon en silence — eux exigent le geste individuel sur leur carte.
        const cibles = initialBlocks.filter((b) => !isAlwaysFullBlock(b));
        setForcedDensity(new Map(cibles.map((b) => [b.uid, 'half'])));
        setRefusedHalves(new Set());   // nouvelles demandes → le moteur reteste
        lastAdjustedRef.current = null;
        relancerCycle();
    }, [initialBlocks, snapshotForRollback, relancerCycle]);

    const onGlobalWidthAuto = useCallback(() => {
        snapshotForRollback();
        setForcedDensity(new Map());
        setRefusedHalves(new Set());
        lastAdjustedRef.current = null;
        relancerCycle();
    }, [snapshotForRollback, relancerCycle]);

    // Mode intégré : applique l'ordre diffusé par la barre du parent. Les
    // gestionnaires passent par une ref : seul un nouvel ordre (n) déclenche.
    const gestionnairesRef = useRef(null);
    gestionnairesRef.current = {
        mode: (v) => { if (v !== densityMode) onToggleMode(); },
        zoom: onGlobalZoom,
        demi: onGlobalHalf,
        auto: onGlobalWidthAuto,
        reponse: onGlobalReponse,
        reset: onResetLayout,
    };
    const ordreN = ordre?.n;
    useEffect(() => {
        if (!ordre) return;
        gestionnairesRef.current[ordre.type]?.(ordre.valeur);
    }, [ordreN]); // eslint-disable-line react-hooks/exhaustive-deps

    const hasAdjustments = manualZoom.size > 0 || forcedDensity.size > 0
        || densityMode === 'compact'
        || reponseMode === 'libre' || forcedReponse.size > 0;

    // Contrôleur passé au SectionRenderer (identique dans les 2 phases)
    const layoutCtl = useMemo(() => ({
        effectiveFor,
        manualFor,
        forcedFor,
        refusedFor,
        reponseFor,
        reponseGlobal: reponseMode,
        onBump,
        onToggleDensity,
        onToggleReponse,
    }), [effectiveFor, manualFor, forcedFor, refusedFor, reponseFor, reponseMode, onBump, onToggleDensity, onToggleReponse]);

    // ⚠️ PAS d'effet « reset des refs » ici : un tel effet s'exécute APRÈS le
    // commit qui vient de remplir les refs de la passe courante → il les
    // efface, la mesure lit des hauteurs nulles et TOUTE l'éval est empilée
    // sur la page 1 (bug page blanche +296px, télémétrie « sections: » vide).
    // Les callbacks de ref inline suffisent : React appelle old(null) puis
    // new(el) à chaque commit — le tableau reflète toujours le dernier rendu,
    // et la mesure itère sur `sections` (les index au-delà sont ignorés).

    // Compteur d'itérations safety
    const iterationRef = useRef(0);
    const MAX_ITERATIONS = 5;

    // Phase mesure → mesure → si overflow détecté : split/scale/reduceMaxHalves
    //              → re-mesure, sinon : pagination → phase display
    useEffect(() => {
        if (phase === 'measure' && !sections.length && currentBlocks.length > 0) {
            // Jalon gel n°3 : des blocs existent mais aucune section n'est composée
            // → l'effect va early-return et la page resterait en "Préparation".
            // eslint-disable-next-line no-console
            console.warn(`[DOM-pagination] ⚠️ phase measure avec 0 section composée pour ${currentBlocks.length} bloc(s) — la page restera en préparation`);
        }
        if (phase !== 'measure' || !sections.length) return;
        let cancelled = false;

        (async () => {
            // 1. Attendre commit React + KaTeX trigger
            await new Promise((r) =>
                requestAnimationFrame(() => requestAnimationFrame(r))
            );
            // 2. Fonts (custom + KaTeX)
            if (document.fonts && document.fonts.ready) {
                await document.fonts.ready;
            }
            // 3. Images — avec timeout de garde : une URL qui pend (presignée
            //    expirée, réseau lent) ne doit jamais geler la mesure.
            const root = measureRootRef.current;
            if (root) {
                const imgs = Array.from(root.querySelectorAll('img'));
                // Chargement PUIS décodage (img.decode) : `load` peut résoudre
                // avant que les dimensions finales soient disponibles pour le
                // layout — decode garantit une mesure aux bonnes hauteurs.
                // Une image morte déclenche onError → PrintImg la remplace par
                // un placeholder de hauteur fixe (mesure stable, prof averti).
                const attenteImages = Promise.allSettled(
                    imgs.map(async (img) => {
                        if (!img.complete) {
                            await new Promise((r) => {
                                img.addEventListener('load', r, { once: true });
                                img.addEventListener('error', r, { once: true });
                            });
                        }
                        if (img.decode) {
                            await img.decode().catch(() => {});
                        }
                    })
                );
                const timeoutImages = new Promise((r) => setTimeout(() => r('timeout'), 8000));
                if (await Promise.race([attenteImages, timeoutImages]) === 'timeout') {
                    const pendantes = imgs.filter((img) => !img.complete).map((img) => img.src);
                    // eslint-disable-next-line no-console
                    console.warn('[DOM-pagination] ⚠️ images non chargées après 8s — mesure quand même', pendantes);
                }
                // Laisser React commettre les éventuels placeholders d'erreur
                // avant la mesure (setState onError → re-render).
                await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
            }
            if (cancelled) return;

            // 4a. Budgets — chrome MESURÉ (plus de constantes devinées).
            //    Overhead par page = padding de .ep-page + header réel (avec sa
            //    marge basse) + marge basse de la .ep-page (consommée avant le
            //    saut de page). Le footer n'entre pas : il s'imprime sur la
            //    page corrections (CorrectionsSection = page-break-before).
            //    Calculés AVANT le scan de refus : le refus VERTICAL compare la
            //    hauteur d'un bloc au budget d'une page.
            const chromeH = chromeProbeRef.current?.getBoundingClientRect().height
                || FALLBACK_CHROME_H;
            const studentH = isEntrainement
                ? 0
                : (studentProbeRef.current?.getBoundingClientRect().height || FALLBACK_STUDENT_H);
            const pageEl = root?.querySelector('.ep-page');
            const pageCS = pageEl ? getComputedStyle(pageEl) : null;
            const pagePad = pageCS
                ? (parseFloat(pageCS.paddingTop) || 0) + (parseFloat(pageCS.paddingBottom) || 0)
                : 18;
            const pageMb = pageCS ? (parseFloat(pageCS.marginBottom) || 0) : 16;

            const pageOverhead = pagePad + pageMb + chromeH;
            const firstBudget = USABLE_PAGE_HEIGHT - pageOverhead - studentH - SAFETY;
            const otherBudget = USABLE_PAGE_HEIGHT - pageOverhead - SAFETY;

            // 4b. Refus de demi-colonne : un bloc forcé `half` est REFUSÉ si
            //     (HORIZONTAL) son contenu à largeur intrinsèque (tableau, SVG,
            //     KaTeX) dépasse sa colonne, OU si (VERTICAL) sa hauteur seule
            //     dépasse une page entière — une colonne de 10 QCM ne tiendra
            //     JAMAIS : sans refus, le moteur n'a aucun coup à jouer et
            //     pagine un monstre que le rastériseur découpe n'importe où
            //     (pages blanches + en-têtes orphelins, constatés sur PDF).
            //     Refusé → repasse full → le split de groupe redevient possible.
            //     Mémoïsé par 'rootUid@facteur'. Scan AVANT rebalance (le DOM
            //     reflète encore la répartition rendue). Le repli du texte
            //     (moche mais légal) reste le jugement du prof.
            const nouveauxRefus = [];
            sections.forEach((section, i) => {
                if (section?.type !== 'masonry') return;
                const el = sectionRefs.current[i];
                if (!el) return;
                const cols = el.querySelectorAll('.ep-masonry > .ep-col');
                if (cols.length !== 2) return;
                [section.leftCol, section.rightCol].forEach((blocksCol, c) => {
                    const kids = Array.from(cols[c].children);
                    if (kids.length !== blocksCol.length) return;
                    blocksCol.forEach((b, k) => {
                        const raw = resolveUidSetting(forcedDensity, b.uid);
                        if (raw !== 'half') return;
                        const kid = kids[k];
                        const tropLarge = kid.scrollWidth > kid.clientWidth + 2;
                        const tropHaut = kid.getBoundingClientRect().height > otherBudget;
                        if (tropLarge || tropHaut) {
                            const key = `${rootUidOf(b.uid)}@${effectiveFor(b.uid)}`;
                            if (!refusedHalves.has(key)) nouveauxRefus.push(key);
                        }
                    });
                });
            });
            if (nouveauxRefus.length > 0) {
                // eslint-disable-next-line no-console
                console.warn('[DOM-pagination] demi refusée (débordement) :', nouveauxRefus);
                setRefusedHalves((prev) => new Set([...prev, ...nouveauxRefus]));
                return; // recompose (le bloc repasse full) puis re-mesure
            }

            // 4c. Mesurer chaque section — itération sur `sections` (source de
            //    vérité), refs lues par index. Pour les masonry : rééquilibrage
            //    des 2 colonnes sur les hauteurs RÉELLES des blocs, puis la
            //    hauteur de section est recalculée sur la nouvelle répartition.
            const heights = sections.map((section, i) => {
                const el = sectionRefs.current[i];
                if (!el) {
                    // eslint-disable-next-line no-console
                    console.warn(`[DOM-pagination] ⚠️ ref manquante pour la section ${i} — hauteur 0`);
                    return 0;
                }
                if (section?.type === 'masonry') {
                    const rebalanced = rebalanceMasonryFromDom(section, el, SECTION_GAP);
                    if (rebalanced != null) return rebalanced;
                }
                return el.getBoundingClientRect().height;
            });

            // DEBUG — télémetrie mesure : à comparer au warn post-display en
            // cas de page blanche (divergence mesure/rendu).
            // eslint-disable-next-line no-console
            console.log(
                `[DOM-pagination] budgets first=${Math.round(firstBudget)} other=${Math.round(otherBudget)} `
                + `(chrome=${Math.round(chromeH)}, student=${Math.round(studentH)}, pad=${Math.round(pagePad)}, mb=${Math.round(pageMb)}) — sections: `
                + heights.map((h, i) => `[${i}:${sections[i]?.type}=${Math.round(h)}px]`).join(' ')
            );

            // 6. Détection overflow — on ne splitte/scale QUE si une section
            //    dépasse la place qu'elle aurait seule sur une page entière.
            //    Si la section rentre seule sur sa page (entre budget et
            //    pageAlone), on la laisse intacte — paginateByHeights la
            //    placera seule sur sa page.
            const pageAloneCapacity = otherBudget;
            const fixes = findAllOversizedFixes(
                sections, heights, pageAloneCapacity, (block) => effectiveFor(block.uid)
            );
            // Sortie de boucle DÉFINIE : borne atteinte avec des débordements
            // résiduels après une action prof → rollback COMPLET (réglages +
            // moteur) vers l'état d'avant le clic — jamais un badge qui ment
            // sur le rendu. Sans action prof (1er rendu), on retombe sur le
            // comportement historique : pagination forcée + warns (ligne de
            // base universelle).
            if (fixes.length > 0 && iterationRef.current >= MAX_ITERATIONS && rollbackRef.current) {
                const snap = rollbackRef.current;
                rollbackRef.current = null;
                iterationRef.current = 0;
                // eslint-disable-next-line no-console
                console.warn('[DOM-pagination] non-convergence après réglage prof — rollback complet');
                setManualZoom(snap.manualZoom);
                setForcedDensity(snap.forcedDensity);
                setReponseMode(snap.reponseMode);
                setForcedReponse(snap.forcedReponse);
                setScaledBlocks(snap.scaledBlocks);
                setMaxHalves(snap.maxHalves);
                setCurrentBlocks(snap.currentBlocks);
                setRefusedHalves(snap.refusedHalves);
                setLayoutNotice('Réglage annulé — il ne permettait pas une mise en page sans débordement.');
                lastAdjustedRef.current = null;
                return; // l'état restauré relance un cycle qui convergeait déjà
            }
            if (iterationRef.current < MAX_ITERATIONS) {
                if (fixes.length > 0) {
                    iterationRef.current += 1;

                    // Applique tous les splits dans le même setState (stable car
                    // chaque targetBlock est une référence unique d'origine)
                    let newBlocks = currentBlocks;
                    let newScaledBlocks = scaledBlocks;
                    let needReduceMaxHalves = false;
                    for (const fix of fixes) {
                        if (fix.type === 'split') {
                            newBlocks = replaceBlock(newBlocks, fix.targetBlock, fix.replacement);
                        } else if (fix.type === 'scale') {
                            if (newScaledBlocks === scaledBlocks) {
                                newScaledBlocks = new Map(scaledBlocks);
                            }
                            newScaledBlocks.set(fix.targetUid, fix.factor);
                        } else if (fix.type === 'reduceMaxHalves') {
                            needReduceMaxHalves = true;
                        }
                    }

                    // Garde anti-gel : la boucle de mesure n'est relancée QUE par un
                    // setState qui change RÉELLEMENT une valeur (React bail-out si
                    // identique). Deux pièges couverts :
                    //   - split dont la cible est introuvable → replaceBlock no-op ;
                    //   - reduceMaxHalves alors que maxHalves est déjà au plancher (2)
                    //     → setMaxHalves(2→2) = bail-out React, aucun re-render.
                    // Dans ces cas, un return figerait la phase 'measure' pour
                    // toujours : on force la pagination, comme à MAX_ITERATIONS.
                    const reduceUtile = needReduceMaxHalves && maxHalves > 2;
                    const rienNAChange = newBlocks === currentBlocks
                        && newScaledBlocks === scaledBlocks
                        && !reduceUtile;
                    if (rienNAChange && rollbackRef.current) {
                        // Le moteur est À COURT DE COUPS après une action prof
                        // (peu importe le compteur d'itérations) : paginer le
                        // layout cassé imprimerait des pages découpées n'importe
                        // où — on annule le réglage, comme à MAX_ITERATIONS.
                        const snap = rollbackRef.current;
                        rollbackRef.current = null;
                        iterationRef.current = 0;
                        // eslint-disable-next-line no-console
                        console.warn('[DOM-pagination] moteur à court de coups après réglage prof — rollback complet', fixes);
                        setManualZoom(snap.manualZoom);
                        setForcedDensity(snap.forcedDensity);
                        setReponseMode(snap.reponseMode);
                        setForcedReponse(snap.forcedReponse);
                        setScaledBlocks(snap.scaledBlocks);
                        setMaxHalves(snap.maxHalves);
                        setCurrentBlocks(snap.currentBlocks);
                        setRefusedHalves(snap.refusedHalves);
                        setLayoutNotice('Réglage annulé — il ne permettait pas une mise en page sans débordement.');
                        lastAdjustedRef.current = null;
                        return;
                    }
                    if (rienNAChange) {
                        // eslint-disable-next-line no-console
                        console.warn('[DOM-pagination] ⚠️ fixes sans effet réel (cible introuvable ou masonry déjà au plancher maxHalves=2) — pagination forcée pour éviter le gel', fixes);
                    } else {
                        if (newBlocks !== currentBlocks) setCurrentBlocks(newBlocks);
                        if (newScaledBlocks !== scaledBlocks) setScaledBlocks(newScaledBlocks);
                        if (reduceUtile) {
                            setMaxHalves((prev) => Math.max(2, prev - 1));
                        }
                        // Pas de wipe des refs : le prochain commit (déclenché
                        // par les setState ci-dessus) ré-attache les callbacks
                        // et la mesure itère sur les nouvelles `sections`.
                        return;
                    }
                }
            }

            // 7. Tout rentre (ou max itérations atteint) → pagination
            iterationRef.current = 0;
            const { pages: computedPages } = paginateByHeights(
                sections,
                heights,
                firstBudget,
                otherBudget,
                SECTION_GAP
            );
            renumberBlocks(computedPages);

            // 8. Corrigé : MÊME moteur (hauteurs mesurées, budgets mesurés),
            //    mais ordre STRICT (lookahead 0 — un corrigé se lit 1→N) et
            //    items triés par exNum FINAL (post-renumber), hauteurs
            //    raccrochées par id. Deux items « demi » consécutifs sont
            //    appariés côte à côte (duo) — hauteur du duo = max des deux.
            const headingH = corrHeadingProbeRef.current?.getBoundingClientRect().height || 40;
            const hParId = correctionItems.map((it) =>
                corrItemRefs.current[it.id]?.getBoundingClientRect().height || 0
            );
            const ordre = [...correctionItems].sort(
                (a, b) => (a.block.exNum || 0) - (b.block.exNum || 0)
            );
            const unites = [];
            for (let i = 0; i < ordre.length; i++) {
                const it = ordre[i];
                const suivant = ordre[i + 1];
                if (it.half && suivant?.half) {
                    unites.push({
                        ids: [it.id, suivant.id],
                        h: Math.max(hParId[it.id], hParId[suivant.id]),
                    });
                    i += 1;
                } else {
                    unites.push({ ids: [it.id], h: hParId[it.id] });
                }
            }
            const CORRIGE_GAP = 8; // = margin-bottom des .ep-corrections-item
            const { pages: pagesCorr } = paginateByHeights(
                unites.map((u) => u.ids),
                unites.map((u) => u.h),
                otherBudget - headingH,
                otherBudget,
                CORRIGE_GAP,
                0
            );

            // 9. Planche de coups de pouce : MÊME moteur, ordre STRICT
            //    (lookahead 0 — les cartes suivent l'ordre des exercices, le
            //    prof retrouve la sienne avant de découper). Vide si l'option
            //    n'est pas cochée : aucune page ajoutée, document inchangé.
            const aidesHeadingH = cartesAide.length
                ? (aidesHeadingProbeRef.current?.getBoundingClientRect().height || 40)
                : 0;
            const { pages: pagesA } = paginateByHeights(
                cartesAide.map((c) => c.id),
                cartesAide.map((c) => aideItemRefs.current[c.id]?.getBoundingClientRect().height || 0),
                otherBudget - aidesHeadingH,
                otherBudget,
                SECTION_GAP,
                0
            );

            setPages(computedPages);
            setPagesCorrige(pagesCorr.map((p) => p.sections));
            setPagesAides(cartesAide.length ? pagesA.map((p) => p.sections) : null);
            rollbackRef.current = null;   // cycle convergé : l'action prof est actée
            setPhase('display');
        })();

        return () => {
            cancelled = true;
        };
    }, [phase, sections, isEntrainement, scaledBlocks, currentBlocks, maxHalves,
        correctionItems, cartesAide, forcedDensity, refusedHalves, effectiveFor]);

    // Repérage visuel après re-dispatch : flash systématique sur l'exercice
    // réglé, scroll d'ancrage UNIQUEMENT s'il est sorti du viewport.
    useEffect(() => {
        if (phase !== 'display' || !pages) return;
        const root = lastAdjustedRef.current;
        if (!root) return;
        lastAdjustedRef.current = null;
        requestAnimationFrame(() => {
            const slots = Array.from(document.querySelectorAll('.ep-body [data-exo-uid]'));
            const el = slots.find((s) => rootUidOf(s.getAttribute('data-exo-uid')) === root);
            if (!el) return;
            const r = el.getBoundingClientRect();
            const horsViewport = r.bottom < 0 || r.top > window.innerHeight;
            if (horsViewport) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ep-flash');
            setTimeout(() => el.classList.remove('ep-flash'), 1100);
        });
    }, [phase, pages]);

    // Garde-fou de diagnostic (dev) : après le rendu display, alerte si une
    // .ep-page rendue dépasse la hauteur utile A4 — c'est le signe d'une
    // dérive mesure/rendu qui produirait une page blanche à l'impression.
    useEffect(() => {
        if (phase !== 'display' || !pages) return;
        requestAnimationFrame(() => {
            document.querySelectorAll('.ep-body > .ep-page').forEach((el, i) => {
                const h = el.scrollHeight;
                if (h > USABLE_PAGE_HEIGHT) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `[DOM-pagination] ⚠️ .ep-page[${i}] rendue à ${Math.round(h)}px > ${USABLE_PAGE_HEIGHT}px utiles (+${Math.round(h - USABLE_PAGE_HEIGHT)}px) — risque de page blanche au print`
                    );
                }
            });
        });
    }, [phase, pages, pagesCorrige, pagesAides]);

    // Réduction visuelle de la feuille affichée sur écran étroit (hook AVANT
    // les retours conditionnels — ordre des hooks stable).
    const { wrapRef: sheetWrapRef, sheetRef, fit } = useSheetFit(phase === 'display' && Boolean(pages));
    const sheetReduite = fit.scale < 1;

    // Mode intégré : le document parent n'offre « Imprimer » qu'une fois
    // chaque instance paginée (un réglage relance la pagination).
    const pret = phase === 'display' && Boolean(pages);
    useEffect(() => { if (onPret) onPret(pret); }, [pret, onPret]);

    // ─── Rendus conditionnels ──────────────────────────────────────────

    if (!loaded) return <div className="ep-root" />;

    if (!data || !data.selectedQuestions || data.selectedQuestions.length === 0) {
        return (
            <div className="ep-root">
                <EmptyState />
            </div>
        );
    }

    // PHASE MESURE : on rend les sections invisibles dans une .ep-sheet réelle
    // pour mesurer leurs vraies hauteurs (largeur unifiée écran/print).
    if (phase === 'measure' || !pages) {
        return (
            <div className="ep-root">
                {!integre && <PrintButton onReset={onResetLayout} showReset={hasAdjustments} notice={layoutNotice} mode={densityMode} onToggleMode={onToggleMode} onGlobalZoom={onGlobalZoom} onGlobalHalf={onGlobalHalf} onGlobalWidthAuto={onGlobalWidthAuto} reponseMode={reponseMode} onGlobalReponse={onGlobalReponse} />}
                <div
                    className={`ep-sheet ep-measure-only${densityMode === 'compact' ? ' ep-compact' : ''}`}
                    ref={measureRootRef}
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        visibility: 'hidden',
                        pointerEvents: 'none',
                    }}
                >
                    {/* Sondes de chrome — mêmes composants/props qu'en phase
                        display, flow-root pour inclure leurs marges */}
                    <div ref={chromeProbeRef} style={{ display: 'flow-root' }}>
                        <CompactHeader
                            titre={meta.titre}
                            date={meta.date}
                            documentType={meta.documentType}
                        />
                    </div>
                    {!isEntrainement && (
                        <div ref={studentProbeRef} style={{ display: 'flow-root' }}>
                            <StudentFields />
                        </div>
                    )}
                    <div className="ep-page">
                        {sections.map((section, i) => (
                            <div
                                key={`measure-${i}`}
                                className="ep-section-wrap"
                                ref={(el) => { sectionRefs.current[i] = el; }}
                                data-section-idx={i}
                            >
                                <SectionRenderer section={section} saisieMode={saisieMode} ctl={layoutCtl} />
                            </div>
                        ))}
                    </div>
                    {/* Sondes du corrigé — mesurées à la largeur qu'elles
                        auront en display (demi pour les items appariables) */}
                    <div ref={corrHeadingProbeRef} style={{ display: 'flow-root' }}>
                        <CorrigeHeading meta={meta} />
                    </div>
                    {correctionItems.map((it) => (
                        <ol
                            key={`corr-m-${it.id}`}
                            className="ep-corrections-list"
                            style={it.half ? { width: 'calc(50% - 4px)' } : undefined}
                            ref={(el) => { corrItemRefs.current[it.id] = el; }}
                        >
                            <CorrectionNode block={it.block} reponseLibre={reponseFor(it.block.uid) === 'libre'} />
                        </ol>
                    ))}
                    {/* Sondes de la planche de coups de pouce — rien du tout
                        quand l'option est décochée (cartesAide vide). */}
                    {cartesAide.length > 0 && (
                        <>
                            <div ref={aidesHeadingProbeRef} style={{ display: 'flow-root' }}>
                                <PlancheHeading meta={meta} />
                            </div>
                            {cartesAide.map((carte) => (
                                <div
                                    key={`aide-m-${carte.id}`}
                                    style={{ display: 'flow-root' }}
                                    ref={(el) => { aideItemRefs.current[carte.id] = el; }}
                                >
                                    <CarteAide carte={carte} />
                                </div>
                            ))}
                        </>
                    )}
                </div>
                <div className="ep-measure-loading">Préparation de l'aperçu…</div>
            </div>
        );
    }

    // PHASE DISPLAY : pagination calculée, rendu final visible.
    // Numérotation sur les seules parties affichées (mode intégré).
    const nbSujet = parties.sujet ? pages.length : 0;
    const nbCorrige = parties.corrige ? (pagesCorrige?.length || 0) : 0;
    const nbPagesVues = nbSujet + nbCorrige + (parties.aides ? (pagesAides?.length || 0) : 0);
    return (
        <div className="ep-root">
            {!integre && <PrintButton onReset={onResetLayout} showReset={hasAdjustments} notice={layoutNotice} mode={densityMode} onToggleMode={onToggleMode} onGlobalZoom={onGlobalZoom} onGlobalHalf={onGlobalHalf} onGlobalWidthAuto={onGlobalWidthAuto} reponseMode={reponseMode} onGlobalReponse={onGlobalReponse} />}
            {/* Panneau "Évaluation de suivi" (2.3) — type évaluation uniquement,
                monté APRÈS la pagination (numérotation finale), fixed + no-print :
                zéro interférence avec le moteur de mesure.
                Garde sur idclasse : EvalBilan passe un contexte réduit ({nomClasse},
                pour le champ Classe de la feuille) SANS vouloir le panneau —
                les questions EvalNat n'ont pas d'attendus TanoEd à suivre. */}
            {!isEntrainement && data?.contexte?.idclasse ? (
                data.contexte.classeVide ? (
                    /* Classe sans élèves (flag posé par QCMDashboard dans le
                       contexte print) : créer une évaluation de suivi n'a pas
                       de sens sans roster — bandeau à la place du panneau,
                       même emplacement, même règle no-print. */
                    <aside className="ep-suivi-panel">
                        <div className="ep-suivi-entete">
                            <h3 className="ep-suivi-titre">Évaluation de suivi</h3>
                        </div>
                        <p className="ep-suivi-vide">
                            Cette classe est pour le moment vide : l'évaluation de
                            suivi sera disponible quand des élèves auront été
                            importés. Vous pouvez imprimer le sujet normalement.
                        </p>
                    </aside>
                ) : (
                    <PanneauEvalSuivi
                        blocks={currentBlocks}
                        contexte={data.contexte}
                        meta={meta}
                    />
                )
            ) : null}
            <div
                className="ep-sheet-viewport"
                ref={sheetWrapRef}
                style={sheetReduite ? { height: fit.height * fit.scale } : undefined}
            >
            <EvalSheet
                meta={meta}
                compact={densityMode === 'compact'}
                sansBandeau={!parties.sujet}
                innerRef={sheetRef}
                style={sheetReduite
                    ? { transform: `scale(${fit.scale})`, transformOrigin: 'top left', margin: 0 }
                    : undefined}
            >
                {parties.sujet && pages.map((page, pageIdx) => (
                    <div
                        key={`page-${pageIdx}`}
                        className={`ep-page${pageIdx > 0 ? ' ep-page-break' : ''}`}
                    >
                        <CompactHeader
                            titre={meta.titre}
                            date={meta.date}
                            documentType={meta.documentType}
                            pageNum={pageIdx + 1}
                            pageTotal={nbPagesVues}
                        />
                        {/* MÊME structure que la phase mesure (div ep-section-wrap) :
                            les règles .ep-page > * / marges internes s'appliquent
                            aux mêmes éléments dans les deux phases — toute
                            divergence structurelle fausse la pagination. */}
                        {page.sections.map((section, sectionIdx) => (
                            <div key={`s-${sectionIdx}`} className="ep-section-wrap">
                                <SectionRenderer section={section} saisieMode={saisieMode} ctl={layoutCtl} />
                            </div>
                        ))}
                    </div>
                ))}
                {/* Pages du CORRIGÉ — même moteur de pagination, ordre strict.
                    Chaque « unité » = 1 item pleine largeur OU 2 items demi
                    côte à côte (duo). */}
                {parties.corrige && pagesCorrige && pagesCorrige.length > 0 && (() => {
                    const itemsParId = new Map(correctionItems.map((it) => [it.id, it]));
                    const totalPages = nbPagesVues;
                    return pagesCorrige.map((unites, i) => (
                        <div key={`corr-page-${i}`} className="ep-page ep-page-break">
                            <CompactHeader
                                titre={meta.titre}
                                date={meta.date}
                                documentType={meta.documentType}
                                pageNum={nbSujet + i + 1}
                                pageTotal={totalPages}
                            />
                            {i === 0 && <CorrigeHeading meta={meta} />}
                            {unites.map((ids, k) => (
                                ids.length === 2 ? (
                                    <div key={`duo-${ids[0]}-${ids[1]}`} className="ep-corrections-duo">
                                        {ids.map((id) => {
                                            const it = itemsParId.get(id);
                                            return it ? (
                                                <ol key={`c-${id}`} className="ep-corrections-list ep-corrections-demi">
                                                    <CorrectionNode block={it.block} reponseLibre={reponseFor(it.block.uid) === 'libre'} />
                                                </ol>
                                            ) : null;
                                        })}
                                    </div>
                                ) : (
                                    itemsParId.get(ids[0]) ? (
                                        <ol key={`c-${ids[0]}-${k}`} className="ep-corrections-list">
                                            <CorrectionNode
                                                block={itemsParId.get(ids[0]).block}
                                                reponseLibre={reponseFor(itemsParId.get(ids[0]).block.uid) === 'libre'}
                                            />
                                        </ol>
                                    ) : null
                                )
                            ))}
                        </div>
                    ));
                })()}

                {/* Pages de la PLANCHE DE COUPS DE POUCE — chacune en saut de
                    page : une carte ne partage jamais une feuille avec un
                    exercice, sinon l'élève lit l'aide avant d'essayer. */}
                {parties.aides && pagesAides && pagesAides.length > 0 && (() => {
                    const cartesParId = new Map(cartesAide.map((c) => [c.id, c]));
                    const avant = nbSujet + nbCorrige;
                    const totalPages = nbPagesVues;
                    return pagesAides.map((ids, i) => (
                        <div key={`aide-page-${i}`} className="ep-page ep-page-break">
                            <CompactHeader
                                titre={meta.titre}
                                date={meta.date}
                                documentType={meta.documentType}
                                pageNum={avant + i + 1}
                                pageTotal={totalPages}
                            />
                            {i === 0 && <PlancheHeading meta={meta} />}
                            {ids.map((id) => (cartesParId.get(id)
                                ? <CarteAide key={`aide-${id}`} carte={cartesParId.get(id)} />
                                : null))}
                        </div>
                    ));
                })()}
            </EvalSheet>
            </div>
        </div>
    );
};

export default EvalPrintPage;
